<template>
  <div class="b2b-search"
    :class="{
      'b2b-search--flat': flat,
      'b2b-search--flat__bordered': flat && bordered,
      'b2b-search--expanded': $slots.expand && expanded,
    }"
  >
    <div class="b2b-search__wrap" @click.stop="focus">
      <div class="b2b-search__icon">
        <search-icon size="24" />
      </div>

      <input
        v-model="model"
        ref="input"
        class="b2b-search__input"
        :placeholder="`${$t('labels.search')}`"
        @focus="$_onFocusInput" />

      <div v-show="clearable && model" class="b2b-search__clear" @click.stop="$_onClickClear">
        <x-icon size="24" />
      </div>
    </div>

    <div ref="expand" class="b2b-search__extends">
      <slot name="expand" />
    </div>
  </div>
</template>

<script>
import { SearchIcon, XIcon } from 'vue-feather-icons';

import DebounceMixin from '../../../mixins/base/debounce-mixin';
import BreakpointMixin from '../../../mixins/base/breakpoints-mixin';

export default {
  name: 'b2b-search-input',

  components: {
    SearchIcon,
    XIcon,
  },

  mixins: [
    BreakpointMixin(),
    DebounceMixin(),
  ],

  props: {
    clearable: Boolean,
    appendIcon: [Object, String],
    expanded: {
      type: Boolean,
      default: true,
    },
    flat: Boolean,
    bordered: Boolean,
    hideCodeSearch: Boolean,
  },

  methods: {
    focus() {
      this.$_emitSearchFocus();
      this.$refs.input.focus();
    },

    $_onClickClear() {
      this.model = '';
    },

    $_emitSearchFocus() {
      this.$hub.emit('search:focus');
    },

    $_onFocusInput() {
      this.$_emitSearchFocus();
    },

    $_onClickAppendIcon($event) {
      this.$emit('click:append', $event);
    },

    $_expandCheck() {
      if (!this.$slots.expand || !this.expanded) this.$el.style.maxHeight = '48px';
      else {
        const height = this.$refs.expand.scrollHeight || this.$refs.expand.clientHeight;
        this.$el.style.maxHeight = `${48 + height + 1}px`;
      }
    },
  },

  watch: {
    expanded() {
      this.$_expandCheck();
    },
  },

  mounted() {
    this.$_expandCheck();

    this.$_observer = new MutationObserver(() => {
      this.$nextTick(this.$_expandCheck);
    });
    this.$_observer.observe(this.$refs.expand, { childList: true, subtree: true });
  },

  beforeDestroy() {
    this.$_observer.disconnect();
  },
};
</script>

<style lang="scss">
$containerHeight: 48px;
$contentHeight: 24px;

.b2b-search {
  width: 100%;
  min-width: 0;

  padding: 0 16px;
  border: $easyBorder !important;
  border-radius: $easyBorderRadius;
  box-shadow: $boxShadowElevated;

  overflow: hidden;
  transition: all 0.2s;
  max-height: $containerHeight;

  &--flat {
    padding: 0 16px;
    box-shadow: none;

    &__bordered {
      border: 1px solid rgba(0, 0, 0, 0.12);
    }
  }

  &--expanded {
    .b2b-search__icon {
      margin-bottom: 0;
    }
  }

  &__wrap {
    cursor: text;
    display: flex;
    align-items: center;
    flex-direction: row;
    height: $containerHeight;
    width: 100%;
    min-width: 0;
  }

  &__extends {
    padding: 8px 0;
    border-top: 1px solid $colorMainSecondary;
  }

  &__icon {
    flex: 0 0 auto;
    height: $contentHeight;
    margin-right: calc(($containerHeight - $contentHeight) / 2);
    margin-bottom: 30px;
    padding-right: calc(($containerHeight - $contentHeight) / 2);

    color: $colorMainSecondary;
  }

  &__clear {
    cursor: pointer;
    flex: 0 0 auto;
    height: $contentHeight;
    margin-left: calc(($containerHeight - $contentHeight) / 2);
    margin-bottom: 30px;
  }

  &__append-icon {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    cursor: pointer;
    height: $contentHeight;
    margin-left: calc(($containerHeight - $contentHeight) / 2);
    white-space: nowrap;
  }

  &__input {
    background-color: transparent;
    width: 100%;
    min-width: 0;
    height: $containerHeight;
    padding-bottom: 5px;

    display: block;

    font-size: 18px;
    color: $easyColorWhite;
    caret-color: $easyColorWhite;

    border: none;

    &::placeholder {
      color: $colorMainSecondary;
    }
  }
}

@media (max-width: 768px) {
  .b2b-search {
    padding: 0 12px;

    &--flat {
      padding: 0 12px;
    }

    &__icon {
      margin-right: 8px;
      padding-right: 8px;
    }

    &__clear,
    &__append-icon {
      margin-left: 8px;
    }

    &__append-icon p {
      display: none;
    }

    &__input {
      font-size: 16px;
    }
  }
}

@media (max-width: 600px) {
  .b2b-search {
    &__icon {
      margin-top: 25px;
    }

    &__clear {
      margin-top: 25px;
    }
  }
}

@media (max-width: 480px) {
  .b2b-search {
    padding: 0 10px;

    &--flat {
      padding: 0 10px;
    }

    &__icon {
      margin-right: 6px;
      padding-right: 6px;
    }

    &__clear,
    &__append-icon {
      margin-left: 6px;
    }
  }
}

.b2b-search:hover {
  border-color: $easyColorSecondary !important;
}

.b2b-search:focus-within {
  border-color: $easyColorSecondary !important;
}
</style>
